import React from 'react';
import { useTranslation } from 'react-i18next';
import './ProfileProgress.css';

// Props interface for ProfileProgress component
export interface ProfileProgressProps {
  completion?: number;
  phase?: number;
  language?: string;
}

// Phase class type for styling
type PhaseClass = 'progress--phase1-low' | 'progress--phase1-high' | 'progress--phase2';

/**
 * ProfileProgress Component
 * 
 * Animated progress indicator for profile completion.
 * Shows progress bar with phase-specific styling and motivating messages.
 * 
 * @example
 * <ProfileProgress completion={65} phase={1} language="en" />
 */
const ProfileProgress: React.FC<ProfileProgressProps> = ({ 
  completion = 0, 
  phase = 1, 
  language = 'en' 
}) => {
  const { t } = useTranslation('profile');

  // Determine phase-specific styling
  const getPhaseClass = (): PhaseClass => {
    if (completion < 35) return 'progress--phase1-low';
    if (completion < 50) return 'progress--phase1-high';
    return 'progress--phase2';
  };

  // Get motivating message based on completion
  const getMessage = (): string => {
    const percent = Math.round(completion);
    if (completion < 35) return t('progress.start', { percent });
    if (completion < 50) return t('progress.almostThere', { percent });
    if (completion < 100) return t('progress.ready', { percent });
    return t('progress.complete');
  };

  // Get icon based on phase
  const getIcon = (): string => {
    if (completion < 35) return '📝';
    if (completion < 50) return '🎯';
    if (completion < 100) return '✨';
    return '🎉';
  };

  return (
    <div className={`profile-progress ${getPhaseClass()}`}>
      <div className="progress-content">
        <div className="progress-info">
          <span className="progress-icon">{getIcon()}</span>
          <span className="progress-message">{getMessage()}</span>
        </div>
      </div>
      <div className="progress-bar-container">
        <div 
          className="progress-bar-fill" 
          style={{ width: `${completion}%` }}
        >
          <div className="progress-shine"></div>
        </div>
      </div>
    </div>
  );
};

export default ProfileProgress;
